import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Chip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Visibility, Edit, FileDownload } from '@mui/icons-material';
import { getNegotiationPending, getNegotiationCompleted, exportNegotiationPending, exportNegotiationCompleted, getUsers } from '../services/api';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';

function NegotiationCost({ user }) {
  const [pendingInfluencers, setPendingInfluencers] = useState([]);
  const [completedInfluencers, setCompletedInfluencers] = useState([]);
  const [inProgressInfluencers, setInProgressInfluencers] = useState([]);
  const [notInterestedInfluencers, setNotInterestedInfluencers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedInfluencer, setSelectedInfluencer] = useState(null);
  const [statusData, setStatusData] = useState({ status: '', negotiation_remarks: '' });
  const [users, setUsers] = useState([]);
  const [userFilter, setUserFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [filteredPendingCount, setFilteredPendingCount] = useState(0);
  const [filteredCompletedCount, setFilteredCompletedCount] = useState(0);
  const [filteredInProgressCount, setFilteredInProgressCount] = useState(0);
  const [filteredNotInterestedCount, setFilteredNotInterestedCount] = useState(0);
  const [totalInstagramSavings, setTotalInstagramSavings] = useState(0);
  const [costData, setCostData] = useState({
    reelCost: '',
    youtubeShortsCoast: '',
    youtubeVideoCost: '',
    remarks: ''
  });
  const [editCompletedModalOpen, setEditCompletedModalOpen] = useState(false);
  const [editCompletedData, setEditCompletedData] = useState({ reelCost: '', remarks: '', originalReelCost: '' });
  const [remarksModalOpen, setRemarksModalOpen] = useState(false);
  const [remarksModalData, setRemarksModalData] = useState([]);
  const [remarksModalName, setRemarksModalName] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchPendingInfluencers();
    fetchCompletedInfluencers();
    fetchInProgressInfluencers();
    fetchNotInterestedInfluencers();
    fetchUsers();
  }, []);

  useEffect(() => {
    updateFilteredCounts();
  }, [pendingInfluencers, completedInfluencers, inProgressInfluencers, notInterestedInfluencers, userFilter, stateFilter, cityFilter]);

  const matchesFilters = (inf) => {
    if (userFilter && !(inf.onboarderId && inf.onboarderId._id === userFilter)) return false;
    if (stateFilter && inf.state !== stateFilter) return false;
    if (cityFilter && inf.city !== cityFilter) return false;
    return true;
  };

  const fetchUsers = async () => {
    try {
      const response = await getUsers();
      setUsers(response.data.filter(user => user.role !== 'superadmin' && user.role !== 'super_admin'));
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const updateFilteredCounts = () => {
    const filteredPending = pendingInfluencers.filter(matchesFilters);
    const filteredCompleted = completedInfluencers.filter(matchesFilters);
    const filteredInProgress = inProgressInfluencers.filter(matchesFilters);
    const filteredNotInterested = notInterestedInfluencers.filter(matchesFilters);
    setFilteredPendingCount(filteredPending.length);
    setFilteredCompletedCount(filteredCompleted.length);
    setFilteredInProgressCount(filteredInProgress.length);
    setFilteredNotInterestedCount(filteredNotInterested.length);

    const rowscount = filteredCompleted.length || 1;
    const totalSavingsPercentage = filteredCompleted.reduce((sum, inf) => {
      const reelCost = Number(inf.reelCost) || 0;
      const negotiatedCost = Number(inf.negotiationCostData?.reelCost) || 0;
      if (reelCost === 0) return sum;
      return sum + ((reelCost - negotiatedCost) / reelCost) * 100;
    }, 0);
    setTotalInstagramSavings(totalSavingsPercentage / rowscount);
  };

  const fetchPendingInfluencers = async () => {
    setLoading(true);
    try {
      const response = await getNegotiationPending();
      setPendingInfluencers(response.data.data);
    } catch (error) {
      console.error('Failed to fetch pending influencers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompletedInfluencers = async () => {
    try {
      const response = await getNegotiationCompleted();
      setCompletedInfluencers(response.data.data);
    } catch (error) {
      console.error('Failed to fetch completed influencers:', error);
    }
  };

  const fetchInProgressInfluencers = async () => {
    try {
      const { default: api } = await import('../services/api');
      const response = await api.get('/negotiation/in-progress');
      setInProgressInfluencers(response.data?.data || []);
    } catch (error) {
      console.error('Failed to fetch in progress influencers:', error);
    }
  };

  const fetchNotInterestedInfluencers = async () => {
    try {
      const { default: api } = await import('../services/api');
      const response = await api.get('/negotiation/not-interested');
      setNotInterestedInfluencers(response.data?.data || []);
    } catch (error) {
      console.error('Failed to fetch not interested influencers:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'negotiated': return 'success';
      case 'pending': return 'warning';
      case 'in-progress': return 'info';
      case 'not-interested': return 'error';
      default: return 'default';
    }
  };

  const pendingColumns = [
    { field: 'sno', headerName: 'SNO', flex: 0.4, minWidth: 80 },
    { field: 'influencerCode', headerName: 'Influencer ID', width: 100 },
    { field: 'name', headerName: 'Influencer Name', flex: 1, minWidth: 150 },
    { field: 'mobile', headerName: 'Mobile', flex: 1, minWidth: 150 },
    { field: 'instagramLink', headerName: 'Instagram Link', flex: 1, minWidth: 200 },
    { field: 'youtubeLink', headerName: 'YouTube Link', flex: 1, minWidth: 200 },
    // { field: 'assignedTo', headerName: 'Assigned To', flex: 1, minWidth: 150 },
    {
      field: "assignedTo",
      headerName: 'Assigned To',
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => params.row?.onboarderId?.name || 'N/A'
    },
    {
      field: "totalcostinstagram",
      headerName: 'Total Cost Instagram',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const value = params.row?.reelCost;
        if (!value || isNaN(value)) return "-";
        return `₹${Number(value).toLocaleString()}`;
      }
    },
    {
      field: "youtubetype",
      headerName: "Youtube Type",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        return params.row?.youtubeType || '-';
      }
    },
    {
      field: "totalcostyoutube",
      headerName: "Total Cost Youtube",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        let val;
        if (params.row?.youtubeType == 'Shorts') {
          val = params.row?.youtubeShortsCost;
        } else {
          val = params.row?.youtubeVideoCost;
        }
        if (!val) return "-";
        return `₹${val.toLocaleString()}`;
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getStatusColor(params.value)}
          size="small"
          sx={{ textTransform: 'capitalize', cursor: 'pointer' }}
          onClick={() => handleStatusClick(params.row)}
        />
      )
    },
    {
      field: 'negotiation_remarks',
      headerName: 'Remarks',
      flex: 1,
      minWidth: 100,
      renderCell: (params) => {
        const remarks = params.row?.negotiation_remarks;
        if (!remarks) return '-';
        if (typeof remarks === 'string') return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Visibility sx={{ fontSize: 16, color: '#019383', cursor: 'pointer' }} onClick={() => { setRemarksModalName(params.row?.name); setRemarksModalData([{ text: remarks }]); setRemarksModalOpen(true); }} />
          </Box>
        );
        if (!remarks.length) return '-';
        const latest = remarks[remarks.length - 1];
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Visibility sx={{ fontSize: 16, color: '#019383', cursor: 'pointer' }} onClick={() => { setRemarksModalName(params.row?.name); setRemarksModalData(remarks); setRemarksModalOpen(true); }} />
          </Box>
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.8,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            startIcon={<Edit />}
            onClick={() => handleEditClick(params.row)}
          // sx={{ color: '#019383' }}
          >
            Edit
          </Button>
        </Box>
      )
    }
  ];


  const negotiatedColumns = [
    { field: 'sno', headerName: 'SNO', flex: 0.4, minWidth: 80 },
    { field: 'influencerCode', headerName: 'Influencer ID', width: 100 },
    { field: 'name', headerName: 'Influencer Name', flex: 1, minWidth: 150 },
    { field: 'mobile', headerName: 'Mobile', flex: 1, minWidth: 150 },
    { field: 'instagramLink', headerName: 'Instagram Link', flex: 1, minWidth: 200 },
    { field: 'youtubeLink', headerName: 'YouTube Link', flex: 1, minWidth: 200 },
    {
      field: "assignedTo",
      headerName: 'Assigned To',
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => params.row?.onboarderId?.name || 'N/A'
    },
    {
      field: "totalcostinstagram",
      headerName: 'Total Cost Instagram',
      flex: 1,
      minWidth: 160,
      cellClassName: 'instagram-column',
      renderCell: (params) => {
        const value = params.row?.reelCost;
        if (!value || isNaN(value)) return "-";
        return `₹${Number(value).toLocaleString()}`;
      }
    },
    {
      field: "negotiatedreelCost",
      headerName: 'Negotiated Total (Instagram)',
      flex: 1,
      minWidth: 230,
      cellClassName: 'instagram-column',
      renderCell: (params) => {
        const value = params.row?.negotiatedreelCost;
        if (!value || isNaN(value)) return "-";
        return `₹${Number(value).toLocaleString()}`;
      }
    },
    {
      field: "savingsCostInstagram",
      headerName: 'Savings Cost Instagram',
      flex: 1,
      minWidth: 200,
      cellClassName: 'instagram-column',
      renderCell: (params) => {
        const reelCostVal = params.row?.reelCost;
        const negotiatedVal = params.row?.negotiatedreelCost;

        if (!reelCostVal || isNaN(reelCostVal)) return "-";

        let value = reelCostVal - negotiatedVal;
        return `₹${Number(value).toLocaleString()}`;
      }
    },
    {
      field: "savingsInstagramPercentage",
      headerName: 'Savings Percentage Instagram',
      flex: 1,
      minWidth: 230,
      cellClassName: 'instagram-column',
      renderCell: (params) => {
        const reelCostVal = params.row?.reelCost;
        const negotiatedVal = params.row?.negotiatedreelCost;

        if (!reelCostVal || isNaN(reelCostVal)) return "-";

        let savingsCost = reelCostVal - negotiatedVal;
        let percentage = Math.round((savingsCost / reelCostVal) * 100);
        return `${Number(percentage).toLocaleString()}%`;
      }
    },
    {
      field: "youtubetype",
      headerName: "Youtube Type",
      flex: 1,
      minWidth: 150,
      cellClassName: 'youtube-column',
      renderCell: (params) => {
        return params.row?.youtubeType ?? "-";
      }
    },
    {
      field: "totalcostyoutube",
      headerName: "Total Cost Youtube",
      flex: 1,
      minWidth: 150,
      cellClassName: 'youtube-column',
      renderCell: (params) => {

        let val;
        if (params.row?.youtubeType == 'Shorts') {
          val = params.row?.youtubeShortsCost;
        } else {
          val = params.row?.youtubeVideoCost;
        }
        if (!val) return "-";
        return `₹${val.toLocaleString()}`;
      }
    },
    {
      field: "negotiatedyoutube",
      headerName: "Negotiated Total (YouTube)",
      flex: 1,
      minWidth: 230,
      cellClassName: 'youtube-column',
      renderCell: (params) => {
        let val;
        if (params.row?.youtubeType == 'Shorts') {
          val = params.row?.negotiatedYoutubeShortsCost;
        } else {
          val = params.row?.negotiatedYoutubeVideoCost;
        }
        if (!val) return "-";
        return `₹${val.toLocaleString()}`;
      }
    },
    {
      field: "savingsCostYoutube",
      headerName: 'Savings Cost YouTube',
      flex: 1,
      minWidth: 200,
      cellClassName: 'youtube-column',
      renderCell: (params) => {
        let originalVal, negotiatedVal;
        if (params.row?.youtubeType == 'Shorts') {
          originalVal = params.row?.youtubeShortsCost;
          negotiatedVal = params.row?.negotiatedYoutubeShortsCost;
        } else {
          originalVal = params.row?.youtubeVideoCost;
          negotiatedVal = params.row?.negotiatedYoutubeVideoCost;
        }
        if (!originalVal || !negotiatedVal) return "-";
        let value = originalVal - negotiatedVal;
        return `₹${Number(value).toLocaleString()}`;
      }
    },
    {
      field: "savingsPercentageYoutube",
      headerName: 'Savings Percentage YouTube',
      flex: 1,
      minWidth: 230,
      cellClassName: 'youtube-column',
      renderCell: (params) => {
        let originalVal, negotiatedVal;
        if (params.row?.youtubeType == 'Shorts') {
          originalVal = params.row?.youtubeShortsCost;
          negotiatedVal = params.row?.negotiatedYoutubeShortsCost;
        } else {
          originalVal = params.row?.youtubeVideoCost;
          negotiatedVal = params.row?.negotiatedYoutubeVideoCost;
        }
        if (!originalVal || !negotiatedVal) return "-";
        let savingsValue = originalVal - negotiatedVal;
        let percentage = Math.round((savingsValue / originalVal) * 100);
        return `${percentage}%`;
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getStatusColor(params.value)}
          size="small"
          sx={{ textTransform: 'capitalize' }}
        />
      )
    },
    {
      field: 'remarks',
      headerName: 'Remarks',
      flex: 1,
      minWidth: 100,
      renderCell: (params) => (
        <Box sx={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {params.row?.remarks || '-'}
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            startIcon={<Visibility />}
            onClick={() => navigate(`/influencer/${params.row._id}`)}
          >
            View
          </Button>

          {user.role === 'admin' && (
            <Button
              size="small"
              startIcon={<Edit />}
              onClick={() => handleEditCompletedClick(params.row)}
            >
              Edit
            </Button>
          )}
        </Box>
      )
    }
  ];

  const isSuperAdmin = user?.role === 'super_admin';

  const superAdminColumnGroupingModel = [
    {
      groupId: 'basic',
      headerName: 'BASIC INFO',
      headerAlign: 'center',
      children: [{ field: 'socialMediaPlatforms' }, { field: 'sno' }, { field: 'name' }, { field: 'onboarderName' }, { field: 'instagramLink' }, { field: 'youtubeLink' }, { field: 'state' }, { field: 'city' }, { field: 'age' }, { field: 'gender' }, { field: 'language' }]
    },
    {
      groupId: 'business',
      headerName: 'BUSINESS PARAMETER INSTAGRAM',
      headerAlign: 'center',
      children: [
        { field: 'topLocations' }, { field: 'femaleGenderRatio' }, { field: 'maleGenderRatio' },
        { field: 'ageRange18_24' }, { field: 'ageRange25_34' }, { field: 'ageRange35_44' },
        { field: 'ageRange45_54' }, { field: 'ageRange55Plus' }
      ]
    },
    {
      groupId: 'financial',
      headerName: 'FINANCIAL INFO',
      headerAlign: 'center',
      children: [
        { field: 'panNumber' }, { field: 'aadharNumber' }, { field: 'bankAccountNumber' },
        { field: 'ifscCode' }, { field: 'holderName' }, { field: 'bankName' }
      ]
    },
    {
      groupId: 'instagram',
      headerName: 'TECHNICAL PARAMETER INSTAGRAM',
      headerAlign: 'center',
      children: [
        { field: 'instagramInfluencerType' }, { field: 'totalCostInstagram' }, { field: 'instagramFollowers' },
        { field: 'engagementRateInstagram' }, { field: 'averageViewInstagram' }, { field: 'last10ReelsViews' },
        { field: 'last10ReelsReach' }, { field: 'instagramLikes' }, { field: 'instagramComments' },
        { field: 'instagramSaves' }, { field: 'instagramShares' }, { field: 'instagramReposts' }
      ]
    },
    {
      groupId: 'businessYoutube',
      headerName: 'BUSINESS PARAMETER YOUTUBE',
      headerAlign: 'center',
      children: [
        { field: 'topLocationsYoutube' }, { field: 'femaleGenderRatioYoutube' }, { field: 'maleGenderRatioYoutube' },
        { field: 'ageRange18_24Youtube' }, { field: 'ageRange25_34Youtube' }, { field: 'ageRange35_44Youtube' },
        { field: 'ageRange45_54Youtube' }, { field: 'ageRange55PlusYoutube' }
      ]
    },
    {
      groupId: 'youtube',
      headerName: 'TECHNICAL PARAMETER YOUTUBE',
      headerAlign: 'center',
      children: [
        { field: 'youtubeInfluencerType' }, { field: 'totalCostYoutube' }, { field: 'youtubeFollowers' },
        { field: 'engagementRateYoutube' }, { field: 'averageViewYoutube' }, { field: 'last10ShortsViews' },
        { field: 'last10ShortsReach' }, { field: 'youtubeLikes' }, { field: 'youtubeComments' },
        { field: 'youtubeSaves' }, { field: 'youtubeShares' }
      ]
    },
    {
      groupId: 'category',
      headerName: 'INFLUENCER PARAMETER',
      headerAlign: 'center',
      children: [{ field: 'category' }]
    },
    {
      groupId: 'negoation',
      headerName: 'BUDGET PARAMETER',
      headerAlign: 'center',
      children: [{ field: 'negotiationCostInstagram' }, { field: 'negotiationCostYoutube' }]
    },
    {
      groupId: 'other',
      headerName: 'OTHER',
      headerAlign: 'center',
      children: [{ field: 'updatedAt' }, { field: 'actions' }]
    }
  ];

  const calculateEngagementRate = (influencer, platform) => {
    if (!influencer) return 0;
    if (platform === 'instagram') {
      const likes = Number(influencer.instagramLikes) || 0;
      const comments = Number(influencer.instagramComments) || 0;
      const saves = Number(influencer.instagramSaves) || 0;
      const shares = Number(influencer.instagramShares) || 0;
      const reposts = Number(influencer.instagramReposts) || 0;
      const totalReach = Number(influencer.last10ReelsReach);
      if (!totalReach || isNaN(totalReach)) return 0;
      return ((likes + comments + saves + shares + reposts) / totalReach) * 100;
    } else {
      const likes = Number(influencer.youtubeLikes) || 0;
      const comments = Number(influencer.youtubeComments) || 0;
      const shares = Number(influencer.youtubeShares) || 0;
      const totalReach = Number(influencer.last10ShortsReach);
      if (!totalReach || isNaN(totalReach)) return 0;
      return ((likes + comments + shares) / totalReach) * 100;
    }
  };

  const calculateAverageViews = (influencer, platform) => {
    if (!influencer) return 0;
    if (platform === 'instagram') {
      const totalViews = Number(influencer.last10ReelsViews);
      const count = Number(influencer.instagramNoOfPost) || 10;
      if (isNaN(totalViews) || count === 0) return 0;
      return Number((totalViews / count).toFixed(2));
    } else {
      const totalViews = Number(influencer.last10ShortsViews);
      let count;
      if (influencer.youtubeType === 'Shorts') count = Number(influencer.youtubeNoOfShorts) || 10;
      else if (influencer.youtubeType === 'Post') count = Number(influencer.youtubeNoOfPost) || 10;
      else return 0;
      if (isNaN(totalViews) || count === 0) return 0;
      return Number((totalViews / count).toFixed(2));
    }
  };

  const superAdminColumns = [
    { field: 'sno', headerName: 'SNO', width: 80 },
    { field: 'socialMediaPlatforms', headerName: 'Channel (IG/YT)', width: 150 },
    { field: 'name', headerName: 'Name', width: 150 },
    { field: 'instagramLink', headerName: 'Profile Link (IN)', width: 180 },
    { field: 'youtubeLink', headerName: 'Profile Link (YT)', width: 180 },
    { field: 'state', headerName: 'State', width: 130 },
    { field: 'city', headerName: 'City', width: 130 },
    { field: 'age', headerName: 'Age', width: 120 },
    { field: 'gender', headerName: 'Gender', width: 120 },
    { field: 'topLocations', headerName: 'Top Locations (IN)', width: 180, renderCell: (params) => (<div style={{ whiteSpace: 'pre-line' }}>{params.value}</div>) },
    { field: 'femaleGenderRatio', headerName: 'Female Gender Ratio (IN)', width: 150 },
    { field: 'maleGenderRatio', headerName: 'Male Gender Ratio (IN)', width: 150 },
    { field: 'ageRange18_24', headerName: '18-24 Age Range (IN)', width: 150 },
    { field: 'ageRange25_34', headerName: '25-34 Age Range (IN)', width: 150 },
    { field: 'ageRange35_44', headerName: '35-44 Age Range (IN)', width: 150 },
    { field: 'ageRange45_54', headerName: '45-54 Age Range (IN)', width: 150 },
    { field: 'ageRange55Plus', headerName: '55+ Age Range (IN)', width: 150 },
    {
      field: 'engagementRateInstagram',
      headerName: 'Engagement rate per reach',
      width: 200,
      renderCell: (params) => {
        const rate = calculateEngagementRate(params.row.allData, 'instagram');
        return rate > 0 ? Number(rate.toFixed(2)) : '-';
      }
    },
    { field: 'instagramFollowers', headerName: 'Followers', width: 150 },
    {
      field: 'averageViewInstagram',
      headerName: 'Avg Reels Views',
      width: 110,
      renderCell: (params) => {
        const views = calculateAverageViews(params.row.allData, 'instagram');
        return views > 0 ? views.toLocaleString() : '-';
      }
    },
    { field: 'last10ReelsViews', headerName: '30 days Reels Views', width: 150 },
    { field: 'last10ReelsReach', headerName: 'Instagram Reach', width: 150 },
    { field: 'topLocationsYoutube', headerName: 'Top Locations (YT)', width: 180, renderCell: (params) => (<div style={{ whiteSpace: 'pre-line' }}>{params.value}</div>) },
    { field: 'femaleGenderRatioYoutube', headerName: 'Female Gender Ratio (YT)', width: 150 },
    { field: 'maleGenderRatioYoutube', headerName: 'Male Gender Ratio (YT)', width: 150 },
    { field: 'ageRange18_24Youtube', headerName: '18-24 Age Range (YT)', width: 150 },
    { field: 'ageRange25_34Youtube', headerName: '25-34 Age Range (YT)', width: 150 },
    { field: 'ageRange35_44Youtube', headerName: '35-44 Age Range (YT)', width: 150 },
    { field: 'ageRange45_54Youtube', headerName: '45-54 Age Range (YT)', width: 150 },
    { field: 'ageRange55PlusYoutube', headerName: '55+ Age Range (YT)', width: 150 },
    {
      field: 'engagementRateYoutube',
      headerName: 'Engagement rate per reach',
      width: 200,
      renderCell: (params) => {
        const rate = calculateEngagementRate(params.row.allData, 'youtube');
        return rate > 0 ? Number(rate.toFixed(2)) : '-';
      }
    },
    { field: 'youtubeFollowers', headerName: 'Followers', width: 150 },
    {
      field: 'averageViewYoutube',
      headerName: 'Youtube Average',
      width: 110,
      renderCell: (params) => {
        const views = calculateAverageViews(params.row.allData, 'youtube');
        return views > 0 ? views.toLocaleString() : '-';
      }
    },
    { field: 'last10ShortsViews', headerName: 'Youtube Views', width: 150 },
    { field: 'last10ShortsReach', headerName: 'Youtube Reach', width: 150 },
    { field: 'category', headerName: 'Influencer Content Tag (Eg. Beauty, Lifestyle, Food)', width: 250 },
    {
      field: 'negotiationCostInstagram',
      headerName: 'Negotiation Total Cost (IN)',
      width: 220,
      renderCell: (params) => {
        const negotiationCost = params.row.allData?.negotiationCost?.reelCost || 0;
        return negotiationCost > 0 ? `₹${negotiationCost.toLocaleString()}` : '-';
      }
    },
    {
      field: 'negotiationCostYoutube',
      headerName: 'Negotiation Total Cost (YT)',
      width: 220,
      renderCell: (params) => {
        let val;
        if (params.row.allData?.youtubeType === 'Shorts') {
          val = params.row.allData?.negotiationCost?.youtubeShortsCoast || 0;
        } else {
          val = params.row.allData?.negotiationCost?.youtubeVideoCost || 0;
        }
        return val > 0 ? `₹${val.toLocaleString()}` : '-';
      }
    },
    {
      field: 'updatedAt',
      headerName: 'Updated At',
      width: 180,
      renderCell: (params) => {
        if (!params.value) return '-';
        const date = new Date(params.value);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const h = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const hours = h % 12 || 12;
        const ampm = h >= 12 ? 'PM' : 'AM';
        return `${day}/${month}/${year} ${hours}:${minutes} ${ampm}`;
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton size="small" onClick={() => navigate(`/influencer/${params.row._id}`)} sx={{ color: '#019383' }}>
            <Visibility />
          </IconButton>
        </Box>
      )
    }
  ];

  const mapToSuperAdminRow = (influencer, index) => {
    const ocr = influencer?.instagramOcrSchema;
    const ytOcr = influencer?.youtubeOcrSchema;
    return {
      id: influencer._id,
      _id: influencer._id,
      sno: index + 1,
      name: influencer.name,
      updatedAt: influencer.updatedAt,
      socialMediaPlatforms: influencer.socialMediaPlatforms,
      instagramLink: influencer.instagramLink ?? '-',
      youtubeLink: influencer.youtubeLink ?? '-',
      state: influencer.state ?? '-',
      city: influencer.city ?? '-',
      age: influencer.age,
      gender: influencer.gender,
      language: influencer.language,
      category: influencer.category,
      instagramFollowers: influencer.instagramFollowers ?? '-',
      youtubeFollowers: influencer.youtubeFollowers ?? '-',
      last10ReelsViews: influencer.last10ReelsViews ?? '-',
      last10ReelsReach: influencer.last10ReelsReach ?? '-',
      instagramLikes: influencer.instagramLikes ?? '-',
      instagramComments: influencer.instagramComments ?? '-',
      instagramSaves: influencer.instagramSaves ?? '-',
      instagramShares: influencer.instagramShares ?? '-',
      instagramReposts: influencer.instagramReposts ?? '-',
      last10ShortsViews: influencer.last10ShortsViews ?? '-',
      last10ShortsReach: influencer.last10ShortsReach ?? '-',
      youtubeLikes: influencer.youtubeLikes ?? '-',
      youtubeComments: influencer.youtubeComments ?? '-',
      youtubeSaves: influencer.youtubeSaves ?? '-',
      youtubeShares: influencer.youtubeShares ?? '-',
      panNumber: influencer.panNumber ?? '-',
      aadharNumber: influencer.aadharNumber ?? '-',
      bankAccountNumber: influencer.bankAccountNumber ?? '-',
      ifscCode: influencer.ifscCode ?? '-',
      holderName: influencer.holderName ?? '-',
      bankName: influencer.bankName ?? '-',
      onboarderName: influencer.onboarderId?.name ?? '-',
      allData: influencer,
      topLocations: ocr?.topLocations?.length
        ? ocr.topLocations.map(loc => `${loc.name} - ${loc.percentage}%`).join('\n') : '-',
      femaleGenderRatio: ocr?.genderDistribution?.female != null ? ocr.genderDistribution.female + '%' : '-',
      maleGenderRatio: ocr?.genderDistribution?.male != null ? ocr.genderDistribution.male + '%' : '-',
      ageRange18_24: ocr?.ageDistribution?.[1]?.percentage != null ? ocr.ageDistribution[1].percentage + '%' : '-',
      ageRange25_34: ocr?.ageDistribution?.[2]?.percentage != null ? ocr.ageDistribution[2].percentage + '%' : '-',
      ageRange35_44: ocr?.ageDistribution?.[3]?.percentage != null ? ocr.ageDistribution[3].percentage + '%' : '-',
      ageRange45_54: ocr?.ageDistribution?.[4]?.percentage != null ? ocr.ageDistribution[4].percentage + '%' : '-',
      ageRange55Plus: ocr?.ageDistribution?.[5]?.percentage != null ? ocr.ageDistribution[5].percentage + '%' : '-',
      topLocationsYoutube: ytOcr?.topLocations?.length
        ? ytOcr.topLocations.map(loc => `${loc.name} - ${loc.percentage}%`).join('\n') : '-',
      femaleGenderRatioYoutube: ytOcr?.genderDistribution?.female != null ? ytOcr.genderDistribution.female + '%' : '-',
      maleGenderRatioYoutube: ytOcr?.genderDistribution?.male != null ? ytOcr.genderDistribution.male + '%' : '-',
      ageRange18_24Youtube: ytOcr?.ageDistribution?.[1]?.percentage != null ? ytOcr.ageDistribution[1].percentage + '%' : '-',
      ageRange25_34Youtube: ytOcr?.ageDistribution?.[2]?.percentage != null ? ytOcr.ageDistribution[2].percentage + '%' : '-',
      ageRange35_44Youtube: ytOcr?.ageDistribution?.[3]?.percentage != null ? ytOcr.ageDistribution[3].percentage + '%' : '-',
      ageRange45_54Youtube: ytOcr?.ageDistribution?.[4]?.percentage != null ? ytOcr.ageDistribution[4].percentage + '%' : '-',
      ageRange55PlusYoutube: ytOcr?.ageDistribution?.[5]?.percentage != null ? ytOcr.ageDistribution[5].percentage + '%' : '-',
    };
  };

  const pendingGroupingModel = [
    { groupId: 'basic', headerName: 'BASIC INFO', headerAlign: 'center', children: [{ field: 'sno' }, { field: 'influencerCode' }, { field: 'name' }, { field: 'mobile' }, { field: 'instagramLink' }, { field: 'youtubeLink' }, { field: 'assignedTo' }] },
    { groupId: 'instagram', headerName: 'INSTAGRAM', headerAlign: 'center', children: [{ field: 'totalcostinstagram' }] },
    { groupId: 'youtube', headerName: 'YOUTUBE', headerAlign: 'center', children: [{ field: 'youtubetype' }, { field: 'totalcostyoutube' }] },
    { groupId: 'other', headerName: 'OTHER', headerAlign: 'center', children: [{ field: 'status' }, { field: 'negotiation_remarks' }, { field: 'actions' }] }
  ];

  const completedGroupingModel = [
    { groupId: 'basic', headerName: 'BASIC INFO', headerAlign: 'center', children: [{ field: 'sno' }, { field: 'influencerCode' }, { field: 'name' }, { field: 'mobile' }, { field: 'instagramLink' }, { field: 'youtubeLink' }, { field: 'assignedTo' }] },
    { groupId: 'instagram', headerName: 'INSTAGRAM', headerAlign: 'center', children: [{ field: 'totalcostinstagram' }, { field: 'negotiatedreelCost' }, { field: 'savingsCostInstagram' }, { field: 'savingsInstagramPercentage' }] },
    { groupId: 'youtube', headerName: 'YOUTUBE', headerAlign: 'center', children: [{ field: 'youtubetype' }, { field: 'totalcostyoutube' }, { field: 'negotiatedyoutube' }, { field: 'savingsCostYoutube' }, { field: 'savingsPercentageYoutube' }] },
    { groupId: 'other', headerName: 'OTHER', headerAlign: 'center', children: [{ field: 'status' }, { field: 'remarks' }, { field: 'actions' }] }
  ];

  const superAdminTableSx = {
    '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f5f5f5', fontWeight: 600 },
    '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600 },
    '& .MuiDataGrid-columnHeader': { borderRight: '1px solid #e0e0e0' },
    '& .MuiDataGrid-cell': { borderRight: '1px solid #e0e0e0', py: 1 },
    '& .MuiDataGrid-columnGroupHeader': {
      backgroundColor: '#1a1d29',
      color: '#fff',
      fontWeight: 700,
      fontSize: '13px',
      borderRight: '2px solid #fff'
    },
    '& .MuiDataGrid-columnGroupHeader .MuiDataGrid-columnHeaderTitle': {
      color: '#fff',
      fontWeight: 700
    },
    '& .instagram-column': { backgroundColor: '#e8f4fcb7 !important' },
    '& .youtube-column': { backgroundColor: '#fff5e69a !important' }
  };

  const normalTableSx = {
    '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f8f9fa' },
    '& .instagram-column': { backgroundColor: '#e8f4fcb7 !important' },
    '& .youtube-column': { backgroundColor: '#fff5e69a !important' }
  };

  const allInfluencers = [...pendingInfluencers, ...completedInfluencers, ...inProgressInfluencers, ...notInterestedInfluencers];
  const stateOptions = [...new Set(allInfluencers.map(inf => inf.state).filter(Boolean))].sort();
  const cityOptions = [...new Set(allInfluencers
    .filter(inf => !stateFilter || inf.state === stateFilter)
    .map(inf => inf.city).filter(Boolean))].sort();

  const getFilteredPendingRows = () => {
    return pendingInfluencers
      .filter(matchesFilters)
      .map((influencer, index) => ({
        id: influencer._id,
        sno: index + 1,
        influencerCode: influencer.influencerCode,
        name: influencer.name,
        email: influencer.email,
        mobile: influencer.contactNumber,
        instagramLink: influencer.instagramLink || '-',
        youtubeLink: influencer.youtubeLink || '-',
        state: influencer.state || '-',
        city: influencer.city || '-',
        platformType: influencer.platformType,
        reelCost: influencer.reelCost,
        youtubeVideoCost: influencer.youtubeVideoCost,
        youtubeShortsCost: influencer.youtubeShortsCost,
        youtubeType: influencer.youtubeType,
        category: influencer.category,
        status: 'pending',
        negotiation_remarks: influencer.negotiation_remarks,
        onboarderId: influencer.onboarderId,
        _id: influencer._id
      }));
  };

  const getFilteredCompletedRows = () => {
    return completedInfluencers
      .filter(matchesFilters)
      .map((influencer, index) => ({
        id: influencer._id,
        sno: index + 1,
        influencerCode: influencer.influencerCode,
        name: influencer.name,
        email: influencer.email,
        mobile: influencer.contactNumber,
        instagramLink: influencer.instagramLink || '-',
        youtubeLink: influencer.youtubeLink || '-',
        state: influencer.state || '-',
        city: influencer.city || '-',
        platformType: influencer.platformType,
        reelCost: influencer?.reelCost,
        youtubeVideoCost: influencer?.youtubeVideoCost,
        youtubeShortsCost: influencer?.youtubeShortsCost,
        youtubeType: influencer?.youtubeType,
        negotiatedreelCost: influencer.negotiationCostData?.reelCost,
        negotiatedYoutubeVideoCost: influencer.negotiationCostData?.youtubeVideoCost,
        negotiatedYoutubeShortsCost: influencer.negotiationCostData?.youtubeShortsCoast,
        remarks: influencer?.negotiationCostData?.remarks,
        category: influencer.category,
        cost: 'Negotiated',
        status: 'negotiated',
        onboarderId: influencer.onboarderId,
        _id: influencer._id
      }));
  };

  const getFilteredInProgressRows = () => {
    return inProgressInfluencers
      .filter(matchesFilters)
      .map((influencer, index) => ({
        id: influencer._id,
        sno: index + 1,
        influencerCode: influencer.influencerCode,
        name: influencer.name,
        email: influencer.email,
        mobile: influencer.contactNumber,
        instagramLink: influencer.instagramLink || '-',
        youtubeLink: influencer.youtubeLink || '-',
        state: influencer.state || '-',
        city: influencer.city || '-',
        platformType: influencer.platformType,
        reelCost: influencer.reelCost,
        youtubeVideoCost: influencer.youtubeVideoCost,
        youtubeShortsCost: influencer.youtubeShortsCost,
        youtubeType: influencer.youtubeType,
        category: influencer.category,
        status: 'in-progress',
        negotiation_remarks: influencer.negotiation_remarks,
        onboarderId: influencer.onboarderId,
        _id: influencer._id
      }));
  };

  const getFilteredNotInterestedRows = () => {
    return notInterestedInfluencers
      .filter(matchesFilters)
      .map((influencer, index) => ({
        id: influencer._id,
        sno: index + 1,
        influencerCode: influencer.influencerCode,
        name: influencer.name,
        email: influencer.email,
        mobile: influencer.contactNumber,
        instagramLink: influencer.instagramLink || '-',
        youtubeLink: influencer.youtubeLink || '-',
        state: influencer.state || '-',
        city: influencer.city || '-',
        platformType: influencer.platformType,
        reelCost: influencer.reelCost,
        youtubeVideoCost: influencer.youtubeVideoCost,
        youtubeShortsCost: influencer.youtubeShortsCost,
        youtubeType: influencer.youtubeType,
        category: influencer.category,
        status: 'not-interested',
        negotiation_remarks: influencer.negotiation_remarks,
        onboarderId: influencer.onboarderId,
        _id: influencer._id
      }));
  };

  const handleStatusClick = (influencer) => {
    setSelectedInfluencer(influencer);
    setStatusData({ status: influencer.status, negotiation_remarks: '' });
    setStatusModalOpen(true);
  };

  const handleStatusUpdate = async () => {
    try {
      const { default: api } = await import('../services/api');
      await api.put(`/negotiation/${selectedInfluencer._id}/status`, statusData);
      setStatusModalOpen(false);
      fetchPendingInfluencers();
      fetchCompletedInfluencers();
      fetchInProgressInfluencers();
      fetchNotInterestedInfluencers();
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleEditClick = (influencer) => {
    setSelectedInfluencer(influencer);
    setCostData({
      reelCost: '',
      youtubeShortsCoast: '',
      youtubeVideoCost: '',
      remarks: ''
    });
    setEditModalOpen(true);
  };

  const handleSaveCost = async () => {
    try {
      const { default: api } = await import('../services/api');
      await api.post('/negotiation-cost', {
        influencerId: selectedInfluencer._id,
        ...costData
      });
      setEditModalOpen(false);
      fetchPendingInfluencers();
      fetchCompletedInfluencers();
      fetchInProgressInfluencers();
      fetchNotInterestedInfluencers();
    } catch (error) {
      console.error('Failed to save negotiation cost:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setCostData(prev => ({ ...prev, [field]: value }));
  };

  const getReelCostError = () => {
    if (!costData.reelCost || !selectedInfluencer?.reelCost) return '';
    if (Number(costData.reelCost) > Number(selectedInfluencer.reelCost)) {
      return `Must be ≤ ₹${Number(selectedInfluencer.reelCost).toLocaleString()}`;
    }
    return '';
  };

  const getYoutubeCostError = () => {
    if (selectedInfluencer?.youtubeType == 'Shorts') {
      if (!costData.youtubeShortsCoast || !selectedInfluencer?.youtubeShortsCost) return '';
      if (Number(costData.youtubeShortsCoast) > Number(selectedInfluencer.youtubeShortsCost)) {
        return `Must be ≤ ₹${Number(selectedInfluencer.youtubeShortsCost).toLocaleString()}`;
      }
    } else {
      if (!costData.youtubeVideoCost || !selectedInfluencer?.youtubeVideoCost) return '';
      if (Number(costData.youtubeVideoCost) > Number(selectedInfluencer.youtubeVideoCost)) {
        return `Must be ≤ ₹${Number(selectedInfluencer.youtubeVideoCost).toLocaleString()}`;
      }
    }
    return '';
  };

  const handleEditCompletedClick = (row) => {
    setSelectedInfluencer(row);
    setEditCompletedData({
      originalReelCost: row.reelCost || '',
      reelCost: row.negotiatedreelCost || '',
      remarks: row.remarks || ''
    });
    setEditCompletedModalOpen(true);
  };

  const handleUpdateCompletedCost = async () => {
    try {
      const { default: api } = await import('../services/api');
      await api.put(`/negotiation-cost/${selectedInfluencer._id}`, editCompletedData);
      setEditCompletedModalOpen(false);
      toast.success('Cost updated successfully');
      fetchCompletedInfluencers();
    } catch (error) {
      console.error('Failed to update cost:', error);
      toast.error('Failed to update cost');
    }
  };

  const handleExportSuperAdmin = (tabInfluencers, sheetName, fileName) => {
    try {
      const data = tabInfluencers.map((influencer, index) => {
        const igEngagement = calculateEngagementRate(influencer, 'instagram');
        const ytEngagement = calculateEngagementRate(influencer, 'youtube');
        const igAvgView = calculateAverageViews(influencer, 'instagram');
        const ytAvgView = calculateAverageViews(influencer, 'youtube');
        const ocr = influencer?.instagramOcrSchema;
        const ytOcr = influencer?.youtubeOcrSchema;
        const topLoc = ocr?.topLocations?.length ? ocr.topLocations.map(loc => `${loc.name} - ${loc.percentage}%`).join(', ') : '-';
        const topLocYt = ytOcr?.topLocations?.length ? ytOcr.topLocations.map(loc => `${loc.name} - ${loc.percentage}%`).join(', ') : '-';
        let ytNegCost;
        if (influencer.youtubeType === 'Shorts') {
          ytNegCost = influencer.negotiationCost?.youtubeShortsCoast || 0;
        } else {
          ytNegCost = influencer.negotiationCost?.youtubeVideoCost || 0;
        }
        return {
          'SNO': index + 1,
          'Channel (IG/YT)': influencer.socialMediaPlatforms || '-',
          'Name': influencer.name || '',
          'State': influencer.state || '-',
          'City': influencer.city || '-',
          'Profile Link (IN)': influencer.instagramLink || '-',
          'Profile Link (YT)': influencer.youtubeLink || '-',
          'PAN Number': influencer.panNumber || '-',
          'Aadhar Number': influencer.aadharNumber || '-',
          'Bank Account': influencer.bankAccountNumber || '-',
          'IFSC Code': influencer.ifscCode || '-',
          'Holder Name': influencer.holderName || '-',
          'Bank Name': influencer.bankName || '-',
          'Age': influencer.age || '-',
          'Gender': influencer.gender || '-',
          'Top Locations (IN)': topLoc,
          'Female Gender Ratio (IN)': ocr?.genderDistribution?.female != null ? ocr.genderDistribution.female + '%' : '-',
          'Male Gender Ratio (IN)': ocr?.genderDistribution?.male != null ? ocr.genderDistribution.male + '%' : '-',
          '18-24 Age Range (IN)': ocr?.ageDistribution?.[1]?.percentage != null ? ocr.ageDistribution[1].percentage + '%' : '-',
          '25-34 Age Range (IN)': ocr?.ageDistribution?.[2]?.percentage != null ? ocr.ageDistribution[2].percentage + '%' : '-',
          '35-44 Age Range (IN)': ocr?.ageDistribution?.[3]?.percentage != null ? ocr.ageDistribution[3].percentage + '%' : '-',
          '45-54 Age Range (IN)': ocr?.ageDistribution?.[4]?.percentage != null ? ocr.ageDistribution[4].percentage + '%' : '-',
          '55+ Age Range (IN)': ocr?.ageDistribution?.[5]?.percentage != null ? ocr.ageDistribution[5].percentage + '%' : '-',
          'Engagement Rate per Reach (IN)': igEngagement > 0 ? Number(igEngagement.toFixed(2)) : '-',
          'Instagram Followers': influencer.instagramFollowers || '-',
          'Avg Reels Views': igAvgView > 0 ? Math.floor(igAvgView) : '-',
          '30 days Reels Views': influencer.last10ReelsViews || '-',
          'Instagram Reach': influencer.last10ReelsReach || '-',
          'Instagram Likes': influencer.instagramLikes || '-',
          'Instagram Comments': influencer.instagramComments || '-',
          'Instagram Saves': influencer.instagramSaves || '-',
          'Instagram Shares': influencer.instagramShares || '-',
          'Instagram Reposts': influencer.instagramReposts || '-',
          'Top Locations (YT)': topLocYt,
          'Female Gender Ratio (YT)': ytOcr?.genderDistribution?.female != null ? ytOcr.genderDistribution.female + '%' : '-',
          'Male Gender Ratio (YT)': ytOcr?.genderDistribution?.male != null ? ytOcr.genderDistribution.male + '%' : '-',
          '18-24 Age Range (YT)': ytOcr?.ageDistribution?.[1]?.percentage != null ? ytOcr.ageDistribution[1].percentage + '%' : '-',
          '25-34 Age Range (YT)': ytOcr?.ageDistribution?.[2]?.percentage != null ? ytOcr.ageDistribution[2].percentage + '%' : '-',
          '35-44 Age Range (YT)': ytOcr?.ageDistribution?.[3]?.percentage != null ? ytOcr.ageDistribution[3].percentage + '%' : '-',
          '45-54 Age Range (YT)': ytOcr?.ageDistribution?.[4]?.percentage != null ? ytOcr.ageDistribution[4].percentage + '%' : '-',
          '55+ Age Range (YT)': ytOcr?.ageDistribution?.[5]?.percentage != null ? ytOcr.ageDistribution[5].percentage + '%' : '-',
          'YouTube Type': influencer.youtubeType || '-',
          'Engagement Rate per Reach (YT)': ytEngagement > 0 ? Number(ytEngagement.toFixed(2)) : '-',
          'YouTube Followers': influencer.youtubeFollowers || '-',
          'Avg View (YT)': ytAvgView > 0 ? Math.floor(ytAvgView) : '-',
          'Youtube Views': influencer.last10ShortsViews || '-',
          'Youtube Reach': influencer.last10ShortsReach || '-',
          'YouTube Likes': influencer.youtubeLikes || '-',
          'YouTube Comments': influencer.youtubeComments || '-',
          'YouTube Saves': influencer.youtubeSaves || '-',
          'YouTube Shares': influencer.youtubeShares || '-',
          'Category': influencer.category || '-',
          'Negotiation Total Cost (IN)': influencer.negotiationCost?.reelCost || '-',
          'Negotiation Total Cost (YT)': ytNegCost > 0 ? ytNegCost : '-',
          'Updated At': influencer.updatedAt ? new Date(influencer.updatedAt).toLocaleDateString('en-GB') : '-',
        };
      });
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      XLSX.writeFile(workbook, fileName);
    } catch (error) {
      console.error('Failed to export:', error);
    }
  };

  const handleExportPending = async () => {
    try {
      const rows = getFilteredPendingRows();
      const columnHeaders = ['S.No', 'Influencer ID', 'Name', 'Mobile', 'Instagram Link', 'YouTube Link', 'State', 'City', 'Assigned To', 'Total Cost Instagram', 'Youtube Type', 'Total Cost Youtube', 'Status', 'Remarks', 'Actions'];
      const dataRows = rows.map((row, index) => ([
        index + 1,
        row.influencerCode || '-',
        row.name || '-',
        row.mobile || '-',
        row.instagramLink || '-',
        row.youtubeLink || '-',
        row.state || '-',
        row.city || '-',
        row.onboarderId?.name || '-',
        row.reelCost || '-',
        row.youtubeType || '-',
        row.youtubeType == 'Shorts' ? row.youtubeShortsCost || '-' : row.youtubeVideoCost || '-',
        row.status || '-',
        (() => { const r = row.negotiation_remarks; if (!r) return '-'; if (typeof r === 'string') return r; if (Array.isArray(r) && r.length) return r.map(item => item.text).join(' | '); return '-'; })(),
        ''
      ]));

      const wsData = [columnHeaders, ...dataRows];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      ws['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 6 } },
        { s: { r: 0, c: 7 }, e: { r: 0, c: 7 } },
        { s: { r: 0, c: 8 }, e: { r: 0, c: 9 } },
        { s: { r: 0, c: 10 }, e: { r: 0, c: 12 } }
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Pending');
      XLSX.writeFile(wb, 'pending_negotiations.xlsx');
    } catch (error) {
      console.error('Failed to export pending negotiations:', error);
    }
  };

  const handleExportCompleted = async () => {
    try {
      const rows = getFilteredCompletedRows();
      const columnHeaders = ['S.No', 'Influencer ID', 'Name', 'Mobile', 'Instagram Link', 'YouTube Link', 'State', 'City', 'Assigned To', 'Total Cost Instagram', 'Negotiated Total (Instagram)', 'Savings Cost Instagram', 'Savings % Instagram', 'Youtube Type', 'Total Cost Youtube', 'Negotiated Total (YouTube)', 'Savings Cost YouTube', 'Savings % YouTube', 'Status', 'Remarks', 'Actions'];
      const dataRows = rows.map((row, index) => {
        const reelCost = Number(row.reelCost) || 0;
        const negotiatedReelCost = Number(row.negotiatedreelCost) || 0;
        const savingsInstagram = reelCost - negotiatedReelCost;
        const savingsPercentInstagram = reelCost > 0 ? Math.round((savingsInstagram / reelCost) * 100) : 0;
        let originalYt, negotiatedYt;
        if (row.youtubeType == 'Shorts') {
          originalYt = Number(row.youtubeShortsCost) || 0;
          negotiatedYt = Number(row.negotiatedYoutubeShortsCost) || 0;
        } else {
          originalYt = Number(row.youtubeVideoCost) || 0;
          negotiatedYt = Number(row.negotiatedYoutubeVideoCost) || 0;
        }
        const savingsYt = originalYt - negotiatedYt;
        const savingsPercentYt = originalYt > 0 ? Math.round((savingsYt / originalYt) * 100) : 0;
        return [
          index + 1,
          row.influencerCode || '-',
          row.name || '-',
          row.mobile || '-',
          row.instagramLink || '-',
          row.youtubeLink || '-',
          row.state || '-',
          row.city || '-',
          row.onboarderId?.name || '-',
          reelCost || '-',
          negotiatedReelCost || '-',
          savingsInstagram || '-',
          savingsPercentInstagram ? `${savingsPercentInstagram}%` : '-',
          row.youtubeType || '-',
          originalYt || '-',
          negotiatedYt || '-',
          savingsYt || '-',
          savingsPercentYt ? `${savingsPercentYt}%` : '-',
          row.status || '-',
          row.remarks || '-',
          ''
        ];
      });

      const wsData = [columnHeaders, ...dataRows];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      ws['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 6 } },
        { s: { r: 0, c: 7 }, e: { r: 0, c: 10 } },
        { s: { r: 0, c: 11 }, e: { r: 0, c: 15 } },
        { s: { r: 0, c: 16 }, e: { r: 0, c: 18 } }
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Completed');
      XLSX.writeFile(wb, 'completed_negotiations.xlsx');
    } catch (error) {
      console.error('Failed to export completed negotiations:', error);
    }
  };

  const handleExportInProgress = () => {
    try {
      const rows = getFilteredInProgressRows();
      const columnHeaders = ['S.No', 'Influencer ID', 'Name', 'Mobile', 'Instagram Link', 'YouTube Link', 'State', 'City', 'Assigned To', 'Total Cost Instagram', 'Youtube Type', 'Total Cost Youtube', 'Status', 'Remarks', 'Actions'];
      const dataRows = rows.map((row, index) => ([
        index + 1,
        row.influencerCode || '-',
        row.name || '-',
        row.mobile || '-',
        row.instagramLink || '-',
        row.youtubeLink || '-',
        row.state || '-',
        row.city || '-',
        row.onboarderId?.name || '-',
        row.reelCost || '-',
        row.youtubeType || '-',
        row.youtubeType == 'Shorts' ? row.youtubeShortsCost || '-' : row.youtubeVideoCost || '-',
        row.status || '-',
        (() => { const r = row.negotiation_remarks; if (!r) return '-'; if (typeof r === 'string') return r; if (Array.isArray(r) && r.length) return r.map(item => item.text).join(' | '); return '-'; })(),
        ''
      ]));

      const wsData = [columnHeaders, ...dataRows];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      ws['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 6 } },
        { s: { r: 0, c: 7 }, e: { r: 0, c: 7 } },
        { s: { r: 0, c: 8 }, e: { r: 0, c: 9 } },
        { s: { r: 0, c: 10 }, e: { r: 0, c: 12 } }
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'In Progress');
      XLSX.writeFile(wb, 'in_progress_negotiations.xlsx');
    } catch (error) {
      console.error('Failed to export in-progress negotiations:', error);
    }
  };

  const handleExportNotInterested = () => {
    try {
      const rows = getFilteredNotInterestedRows();
      const columnHeaders = ['S.No', 'Influencer ID', 'Name', 'Mobile', 'Instagram Link', 'YouTube Link', 'State', 'City', 'Assigned To', 'Total Cost Instagram', 'Youtube Type', 'Total Cost Youtube', 'Status', 'Remarks', 'Actions'];
      const dataRows = rows.map((row, index) => ([
        index + 1,
        row.influencerCode || '-',
        row.name || '-',
        row.mobile || '-',
        row.instagramLink || '-',
        row.youtubeLink || '-',
        row.state || '-',
        row.city || '-',
        row.onboarderId?.name || '-',
        row.reelCost || '-',
        row.youtubeType || '-',
        row.youtubeType == 'Shorts' ? row.youtubeShortsCost || '-' : row.youtubeVideoCost || '-',
        row.status || '-',
        (() => { const r = row.negotiation_remarks; if (!r) return '-'; if (typeof r === 'string') return r; if (Array.isArray(r) && r.length) return r.map(item => item.text).join(' | '); return '-'; })(),
        ''
      ]));

      const wsData = [columnHeaders, ...dataRows];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      ws['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 6 } },
        { s: { r: 0, c: 7 }, e: { r: 0, c: 7 } },
        { s: { r: 0, c: 8 }, e: { r: 0, c: 9 } },
        { s: { r: 0, c: 10 }, e: { r: 0, c: 12 } }
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Not Interested');
      XLSX.writeFile(wb, 'not_interested_negotiations.xlsx');
    } catch (error) {
      console.error('Failed to export not-interested negotiations:', error);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
        Negotiation Cost Management
      </Typography>

      <Box sx={{ mb: 3, display: 'flex', gap: 3 }}>
        <Card
          sx={{
            minWidth: 180,
            bgcolor: '#fff3e0',
            height: '83px',
            cursor: 'pointer',
            '&:hover': { bgcolor: '#ffe0b2' }
          }}
          onClick={() => setActiveTab(0)}
        >
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#f57c00' }}>
              {filteredPendingCount}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Pending
            </Typography>
          </CardContent>
        </Card>
        <Card
          sx={{
            minWidth: 180,
            bgcolor: '#fff3e0',
            height: '83px',
            cursor: 'pointer',
            '&:hover': { bgcolor: '#ffe0b2' }
          }}
          onClick={() => setActiveTab(1)}
        >
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#f57c00' }}>
              {filteredInProgressCount}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              In Progress
            </Typography>
          </CardContent>
        </Card>
        <Card
          sx={{
            minWidth: 180,
            bgcolor: '#ffebee',
            height: '83px',
            cursor: 'pointer',
            '&:hover': { bgcolor: '#ffcdd2' }
          }}
          onClick={() => setActiveTab(2)}
        >
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
              {filteredNotInterestedCount}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Not Interested
            </Typography>
          </CardContent>
        </Card>
        <Card
          sx={{
            minWidth: 180,
            bgcolor: '#e8f5e8',
            height: '83px',
            cursor: 'pointer',
            '&:hover': { bgcolor: '#c8e6c8' }
          }}
          onClick={() => setActiveTab(3)}
        >
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
              {filteredCompletedCount}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Completed
            </Typography>
          </CardContent>
        </Card>
        <Card
          sx={{
            minWidth: 180,
            bgcolor: '#e3f2fd',
            height: '83px'
          }}
        >
          <CardContent sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
              {Math.round(totalInstagramSavings)}%
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Avg Savings %
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {(user.role == 'admin' || user.role == 'super_admin') && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Filter by User</InputLabel>
              <Select
                value={userFilter}
                label="Filter by User"
                onChange={(e) => setUserFilter(e.target.value)}
              >
                <MenuItem value="">All Users</MenuItem>
                {users.filter(user => user.role === 'onboarder' || user.role === 'admin').map((user) => (
                  <MenuItem key={user._id} value={user._id}>
                    {user.name || user.username}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Filter by State</InputLabel>
            <Select
              value={stateFilter}
              label="Filter by State"
              onChange={(e) => { setStateFilter(e.target.value); setCityFilter(''); }}
            >
              <MenuItem value="">All States</MenuItem>
              {stateOptions.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Filter by City</InputLabel>
            <Select
              value={cityFilter}
              label="Filter by City"
              onChange={(e) => setCityFilter(e.target.value)}
            >
              <MenuItem value="">All Cities</MenuItem>
              {cityOptions.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
        <Button
          variant="contained"
          startIcon={<FileDownload />}
          onClick={() => {
            if (isSuperAdmin) {
              const tabMap = [
                { data: pendingInfluencers, sheet: 'Pending', file: 'pending_negotiations.xlsx' },
                { data: inProgressInfluencers, sheet: 'In Progress', file: 'in_progress_negotiations.xlsx' },
                { data: notInterestedInfluencers, sheet: 'Not Interested', file: 'not_interested_negotiations.xlsx' },
                { data: completedInfluencers, sheet: 'Completed', file: 'completed_negotiations.xlsx' },
              ];
              const { data, sheet, file } = tabMap[activeTab];
              handleExportSuperAdmin(data.filter(matchesFilters), sheet, file);
            } else {
              (activeTab === 0 ? handleExportPending : activeTab === 1 ? handleExportInProgress : activeTab === 2 ? handleExportNotInterested : handleExportCompleted)();
            }
          }}
        >
          Export {activeTab === 0 ? 'Pending' : activeTab === 1 ? 'In Progress' : activeTab === 2 ? 'Not Interested' : 'Completed'}
        </Button>
      </Box>

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label={`Pending (${filteredPendingCount})`} />
        <Tab label={`In Progress (${filteredInProgressCount})`} />
        <Tab label={`Not Interested (${filteredNotInterestedCount})`} />
        <Tab label={`Completed (${filteredCompletedCount})`} />
      </Tabs>

      {activeTab === 0 && (
        <DataGrid
          columns={isSuperAdmin ? superAdminColumns : pendingColumns}
          rows={loading ? [] : (isSuperAdmin ? pendingInfluencers.filter(matchesFilters).map(mapToSuperAdminRow) : getFilteredPendingRows())}
          loading={loading}
          pageSize={10}
          disableRowSelectionOnClick
          autoHeight
          rowHeight={57}
          {...(isSuperAdmin && { experimentalFeatures: { columnGrouping: true }, columnGroupingModel: superAdminColumnGroupingModel })}
          sx={isSuperAdmin ? superAdminTableSx : normalTableSx}
        />
      )}

      {activeTab === 1 && (
        <DataGrid
          columns={isSuperAdmin ? superAdminColumns : pendingColumns}
          rows={loading ? [] : (isSuperAdmin ? inProgressInfluencers.filter(matchesFilters).map(mapToSuperAdminRow) : getFilteredInProgressRows())}
          loading={loading}
          pageSize={10}
          disableRowSelectionOnClick
          autoHeight
          rowHeight={57}
          {...(isSuperAdmin && { experimentalFeatures: { columnGrouping: true }, columnGroupingModel: superAdminColumnGroupingModel })}
          sx={isSuperAdmin ? superAdminTableSx : normalTableSx}
        />
      )}

      {activeTab === 2 && (
        <DataGrid
          columns={isSuperAdmin ? superAdminColumns : pendingColumns}
          rows={loading ? [] : (isSuperAdmin ? notInterestedInfluencers.filter(matchesFilters).map(mapToSuperAdminRow) : getFilteredNotInterestedRows())}
          loading={loading}
          pageSize={10}
          disableRowSelectionOnClick
          autoHeight
          rowHeight={57}
          {...(isSuperAdmin && { experimentalFeatures: { columnGrouping: true }, columnGroupingModel: superAdminColumnGroupingModel })}
          sx={isSuperAdmin ? superAdminTableSx : normalTableSx}
        />
      )}

      {activeTab === 3 && (
        <DataGrid
          columns={isSuperAdmin ? superAdminColumns : negotiatedColumns}
          rows={loading ? [] : (isSuperAdmin ? completedInfluencers.filter(matchesFilters).map(mapToSuperAdminRow) : getFilteredCompletedRows())}
          loading={loading}
          initialState={{ pagination: { paginationModel: { page: 0, pageSize: 10 } } }}
          pageSizeOptions={[10, 20]}
          disableRowSelectionOnClick
          autoHeight
          rowHeight={57}
          {...(isSuperAdmin && { experimentalFeatures: { columnGrouping: true }, columnGroupingModel: superAdminColumnGroupingModel })}
          sx={isSuperAdmin ? superAdminTableSx : normalTableSx}
        />
      )}

      {/* Status Update Modal */}
      <Dialog open={statusModalOpen} onClose={() => setStatusModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Status - {selectedInfluencer?.name}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusData.status}
              label="Status"
              onChange={(e) => setStatusData(prev => ({ ...prev, status: e.target.value }))}
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="in-progress">In Progress</MenuItem>
              <MenuItem value="not-interested">Not Interested</MenuItem>
            </Select>
          </FormControl>

          {/* {selectedInfluencer?.negotiation_remarks && (
            Array.isArray(selectedInfluencer.negotiation_remarks) && selectedInfluencer.negotiation_remarks.length > 0 ? (
              <Box sx={{ mb: 2, maxHeight: 150, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>Previous Remarks:</Typography>
                {selectedInfluencer.negotiation_remarks.map((r, i) => (
                  <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
                    <Typography variant="caption">{r.text}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {r.date ? new Date(r.date).toLocaleDateString('en-GB') : ''}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : typeof selectedInfluencer.negotiation_remarks === 'string' && selectedInfluencer.negotiation_remarks ? (
              <Box sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>Previous Remarks:</Typography>
                <Typography variant="caption" sx={{ display: 'block', py: 0.5 }}>{selectedInfluencer.negotiation_remarks}</Typography>
              </Box>
            ) : null
          )} */}

          <TextField
            fullWidth
            label="Add Remark"
            multiline
            rows={3}
            value={statusData.negotiation_remarks}
            onChange={(e) => setStatusData(prev => ({ ...prev, negotiation_remarks: e.target.value }))}
            placeholder="Enter new remark..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusModalOpen(false)}>Cancel</Button>
          <Button onClick={handleStatusUpdate} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Remarks Modal */}
      <Dialog open={remarksModalOpen} onClose={() => setRemarksModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Remarks - {remarksModalName}</DialogTitle>
        <DialogContent>
          {remarksModalData.length > 0 ? (
            <Box sx={{ mt: 1 }}>
              {remarksModalData.map((r, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #f0f0f0' }}>
                  <Typography variant="body2">{r.text}</Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ minWidth: 80, textAlign: 'right' }}>
                    {r.date ? new Date(r.date).toLocaleDateString('en-GB') : ''}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">No remarks found.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemarksModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Completed Cost Modal */}
      <Dialog open={editCompletedModalOpen} onClose={() => setEditCompletedModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Cost - {selectedInfluencer?.name}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Total Cost Instagram"
            type="number"
            value={editCompletedData.originalReelCost}
            onChange={(e) => setEditCompletedData(prev => ({ ...prev, originalReelCost: e.target.value }))}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Negotiated Reel Cost"
            type="number"
            value={editCompletedData.reelCost}
            onChange={(e) => setEditCompletedData(prev => ({ ...prev, reelCost: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Remarks"
            multiline
            rows={3}
            value={editCompletedData.remarks}
            onChange={(e) => setEditCompletedData(prev => ({ ...prev, remarks: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditCompletedModalOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateCompletedCost} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Cost Modal */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Negotiation Cost - {selectedInfluencer?.name}</DialogTitle>
        <DialogContent>
          {selectedInfluencer?.reelCost ? (
            <>
              {selectedInfluencer.reelCost && (
                <>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 2, mb: 0.5 }}>
                    Total Cost Instagram
                  </Typography>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                    ₹{Number(selectedInfluencer.reelCost).toLocaleString()}
                  </Typography>
                </>
              )}
              {(selectedInfluencer.youtubeShortsCost || selectedInfluencer.youtubeVideoCost) && (
                <>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 2, mb: 0.5 }}>
                    Total Cost Youtube ({selectedInfluencer.youtubeType || 'Video'})
                  </Typography>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                    ₹{Number(selectedInfluencer.youtubeType == 'Shorts' ? selectedInfluencer.youtubeShortsCost : selectedInfluencer.youtubeVideoCost).toLocaleString()}
                  </Typography>
                </>
              )}
              <TextField
                fullWidth
                label="Reel Cost"
                type="number"
                value={costData.reelCost}
                onChange={(e) => handleInputChange('reelCost', e.target.value)}
                error={!!getReelCostError()}
                helperText={getReelCostError()}
                sx={{ mb: 2 }}
              />
            </>
          ) : null}
          {selectedInfluencer?.youtubeType == 'Shorts' && selectedInfluencer?.youtubeShortsCost ? (
            <TextField
              fullWidth
              label="YouTube Shorts Cost"
              type="number"
              value={costData.youtubeShortsCoast}
              onChange={(e) => handleInputChange('youtubeShortsCoast', e.target.value)}
              error={!!getYoutubeCostError()}
              helperText={getYoutubeCostError()}
              sx={{ mb: 2 }}
            />
          ) : null}
          {selectedInfluencer?.youtubeType != 'Shorts' && selectedInfluencer?.youtubeVideoCost ? (
            <TextField
              fullWidth
              label="YouTube Video Cost"
              type="number"
              value={costData.youtubeVideoCost}
              onChange={(e) => handleInputChange('youtubeVideoCost', e.target.value)}
              error={!!getYoutubeCostError()}
              helperText={getYoutubeCostError()}
              sx={{ mb: 2 }}
            />
          ) : null}
          <TextField
            fullWidth
            label="Additional Remarks (Optional)"
            multiline
            rows={3}
            value={costData.remarks}
            onChange={(e) => handleInputChange('remarks', e.target.value)}
            placeholder="Enter any additional remarks..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditModalOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveCost} variant="contained" disabled={!!getReelCostError() || !!getYoutubeCostError()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default NegotiationCost;